import React, { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import style from './GoogleMap.module.less';
import { GoogleMapProvider } from 'js/context/GoogleMapContext';
import clsx from 'clsx';
import useMapEvent from 'js/components/DataLayer/hooks/useMapEvent';
import { voidFunc } from 'js/constants/PropTypeUtils';
import { fitMapToBounds } from '../../../helpers/MapsUtils';
import APIConstants from '../../../APIConstants';

const useBlankBaseMap = process.env.REACT_APP_E2E_BLANK_MAP === 'true';
const blankMapTypeId = 'e2e-blank';

const getConfig = (props) => {
  return {
    center: props.center,
    zoom: props.zoom,
    optimized: props.optimized,
    mapTypeId: useBlankBaseMap && props.mapType !== 'transparent' ? blankMapTypeId : props.mapType,
    disableDefaultUI: true,
    scrollwheel: true,
    keyboardShortcuts: false,
    disableDoubleClickZoom: false,
    scaleControl: true,
    zoomControl: false,
    tilt: 0,
    // APIConstansts.mapId is set in the .env file: REACT_APP_MAPSTYLEID
    mapId: useBlankBaseMap ? undefined : APIConstants.mapId,
    backgroundColor: 'rgba(0,0,0,0)',
  };
};

const GoogleMap = (props) => {
  const mapConfig = useRef(getConfig(props));
  const googleMapDomRef = useRef(null);
  const initialized = useRef(false);

  const [googleMap, setGoogleMap] = useState(null);

  /* CONFIG AND STYLING */
  useEffect(() => {
    if (googleMap) {
      const config = getConfig(props);
      config.map = googleMap;

      mapConfig.current = config;
      googleMap.setOptions(config);
    }
  }, [props.center, props.zoom, props.optimized, props.mapType, googleMap]);

  /* INITIALIZATION */
  useLayoutEffect(() => {
    if (!initialized.current) {
      const map = new google.maps.Map(googleMapDomRef.current, {
        ...mapConfig.current,
        mapTypeId: useBlankBaseMap ? 'roadmap' : mapConfig.current.mapTypeId,
      });
      if (useBlankBaseMap && props.mapType !== 'transparent') {
        map.mapTypes.set(blankMapTypeId, {
          tileSize: new google.maps.Size(256, 256),
          maxZoom: 22,
          getTile: (_coord, _zoom, document) => {
            const tile = document.createElement('div');
            tile.style.backgroundColor = '#3a473a';
            tile.style.width = '256px';
            tile.style.height = '256px';
            return tile;
          },
          releaseTile: () => {},
        });
        map.setMapTypeId(blankMapTypeId);
      }
      setGoogleMap(map);
    }
  }, []);

  useEffect(() => {
    if (!googleMap || props.mapType === 'transparent') {
      return;
    }

    const mapElement = googleMapDomRef.current;
    const markLoading = () => {
      mapElement.dataset.mapReady = 'false';
    };
    const markReady = () => {
      mapElement.dataset.mapReady = 'true';
    };
    const listeners = [
      googleMap.addListener('center_changed', markLoading),
      googleMap.addListener('zoom_changed', markLoading),
      googleMap.addListener('maptypeid_changed', markLoading),
      googleMap.addListener('tilesloaded', markReady),
    ];

    return () => listeners.forEach((listener) => google.maps.event.removeListener(listener));
  }, [googleMap, props.mapType]);

  const initHandler = useCallback(() => {
    if (googleMap && typeof props.onMapLoaded === 'function') {
      if (googleMap.getBounds() && !initialized.current) {
        props.onMapLoaded(googleMap);
        if (props.fitToBounds) {
          fitMapToBounds(googleMap, props.fitToBounds);
        }
        initialized.current = true;
      }
    }
  }, [googleMap, props.fitToBounds, props.onMapLoaded]);

  useMapEvent(googleMap, 'bounds_changed', initHandler);

  /* MAP SYNCING */
  const syncHandler = useCallback(() => {
    if (googleMap) {
      googleMap.setCenter(props.syncWithMap.getCenter());
      googleMap.setZoom(props.syncWithMap.getZoom());
    }
  }, [props.syncWithMap, googleMap]);

  useMapEvent(props.syncWithMap, 'drag', syncHandler);
  useMapEvent(props.syncWithMap, 'idle', syncHandler);
  useMapEvent(props.syncWithMap, 'zoom_changed', syncHandler);

  useEffect(() => {
    props.onSyncSetup(syncHandler);
  }, [syncHandler]);

  return (
    <div className={clsx({ [style.Root]: true, [style.Disabled]: props.disableInteraction })}>
      <div
        ref={googleMapDomRef}
        data-testid={props.mapType === 'transparent' ? 'google-map-overlay' : 'google-map'}
        data-map-type={props.mapType}
        data-map-ready="false"
        className={clsx({
          [style.GoogleMap]: true,
          [style.MagicWand]: props.isMagicWandSelecting,
          [style.E2EBlankMap]: useBlankBaseMap,
        })}
      />

      <GoogleMapProvider value={{ googleMap, googleMapDomRef: googleMapDomRef.current }}>
        {googleMap && props.children}
      </GoogleMapProvider>
    </div>
  );
};

GoogleMap.propTypes = {
  children: PropTypes.any,
  center: PropTypes.object,
  zoom: PropTypes.number,
  optimized: PropTypes.bool,
  isMagicWandSelecting: PropTypes.bool,
  disableInteraction: PropTypes.bool,
  syncWithMap: PropTypes.object,
  fitToBounds: PropTypes.object,
  mapType: PropTypes.oneOf(['hybrid', 'satellite', 'terrain', 'roadmap', 'transparent']),
  onMapLoaded: PropTypes.func,
  onSyncSetup: PropTypes.func,
};

GoogleMap.defaultProps = {
  onMapLoaded: voidFunc,
  mapType: 'hybrid',
  onSyncSetup: voidFunc,
  optimized: true,
  center: { lat: 0, lng: 0 },
  zoom: 3,
};

export default memo(GoogleMap);
